import { useEffect } from "react"

import { Form, Input, Select, Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"

import ReferralActions from "../../../reducer"

import "./PaymentMethodFormModal.less"

const { Text } = Typography

const customizeRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && <span className="required-mark">*</span>}
  </>
)

const PaymentMethodForm = ({ initialValues, onSubmit, setIsValidForm }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const country = Form.useWatch("country", form)

  const { countryCodes } = useSelector((state) => state.referrals)

  const countries = Object.entries(countryCodes || {}).map(([code, name]) => ({
    id: code,
    name: name
  }))

  const unitedStatesId = countries?.find((c) => c.name === "United States")?.id
  const countrySelected = countries?.find((c) => c.id === country)
  const isUnitedStateSelected = countrySelected && countrySelected.id === unitedStatesId

  const values = Form.useWatch([], form)

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsValidForm(true))
      .catch((err) => {
        if (err?.outOfDate) return
        setIsValidForm(false)
      })
  }, [form, values])

  const handleSubmit = (values) => {
    const clearedValues = {}
    for (const [key, value] of Object.entries(values)) {
      clearedValues[key] = value || null
    }
    onSubmit(values)
  }

  useEffect(() => {
    if (!countryCodes) {
      dispatch(ReferralActions.getCountryCodesRequest())
    }
  }, [])

  return (
    <Form
      id="payment-method-form"
      layout="vertical"
      className="with-default-error"
      onFinish={handleSubmit}
      form={form}
      requiredMark={customizeRequiredMark}
      initialValues={{
        ...initialValues
      }}
    >
      <Text className="sub-title contact-address">Bank account details</Text>
      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Country/region"
        name="country"
        required
      >
        <Select
          showSearch
          className="country-state-select"
          placeholder="e.g, United States"
          optionFilterProp="children"
        >
          {countries.map(({ id, name }) => (
            <Select.Option key={id} value={id}>
              {name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Currency"
        name="currency"
        required
        initialValue={"USD"}
      >
        <Input disabled />
      </Form.Item>
      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Company name"
        name="company_name"
        required
        extra="Please enter the full name as per their bank details to avoid rejection or delays."
      >
        <Input placeholder="e.g, Tesla Inc" />
      </Form.Item>
      {isUnitedStateSelected ? (
        <Form.Item
          rules={[{ required: true, message: "This field is required" }]}
          label={"Account number"}
          name="account_number"
          required
        >
          <Input placeholder="Enter account number..." />
        </Form.Item>
      ) : (
        <Form.Item
          rules={[{ required: true, message: "This field is required" }]}
          label={"IBAN"}
          name="iban"
          required
        >
          <Input placeholder="Enter IBAN..." />
        </Form.Item>
      )}

      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="SWIFT code"
        name="swift_code"
        required
      >
        <Input placeholder="Enter SWIFT code..." />
      </Form.Item>

      <Text className="sub-title contact-address">Contact address</Text>

      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Country of residence"
        name="residence_country"
        required
      >
        <Select
          showSearch
          className="country-state-select"
          placeholder="e.g, United States"
          optionFilterProp="children"
        >
          {(countries || []).map((country) => (
            <Select.Option key={country.id} value={country.id}>
              {country.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Town/City"
        name="city"
        required
      >
        <Input />
      </Form.Item>
      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Street"
        name="street"
        required
      >
        <Input />
      </Form.Item>
      <Form.Item
        rules={[{ required: true, message: "This field is required" }]}
        label="Postcode"
        name="postcode"
        required
      >
        <Input />
      </Form.Item>
    </Form>
  )
}

export default PaymentMethodForm
